import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';
import { faCircle, faFont } from '@fortawesome/free-solid-svg-icons';
import Image from 'next/image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { nanoid } from 'nanoid';
import axios from 'axios';
import Markdown from 'markdown-to-jsx';
import readingTime from 'reading-time';
import dayjs from 'dayjs';
import Footer from '../../src/Components/molecules/Footer/Footer';
import Header from '../../src/Components/molecules/Header/Header';
import PostAvatar from '../../src/Components/molecules/Post/PostAvatar';
import PostTag from '../../src/Components/molecules/Post/PostTag';
import { getPost as IGetPost } from '../../src/lib/api/types';
import ShareRow from '../../src/Components/molecules/ShareRow/ShareRow';
import FounderBlock from '../../src/Components/molecules/FounderBlock/FounderBlock';
import SidebarPost from '../../src/Components/molecules/Sidebar/SidebarPost';
import HeadCustom, { description, siteName } from '../../src/Components/molecules/Head/Head';
import usePostGet from '../../src/hooks/usePostGet';
import { IPostData } from '../../src/Components/molecules/ListPost/ListPost';

const Post: NextPage<IGetPost> = () => {
    const router = useRouter();
    const { id } = router.query;

    const [readMinutes, setReadMinutes] = useState(0);

    const { getPostBySlug } = usePostGet();

    const [post, setPost] = useState<IPostData | null>(null);

    const [content, setContent] = useState<string | null>(null);

    const load = useCallback(async (slug: string) => {
        if (slug !== '') {
            const mdFile = await axios.get(`/content/${slug}.md`);

            setContent(mdFile.data as string);

            setReadMinutes(Math.round(readingTime((mdFile.data as string) ?? '').minutes));
        }
    }, []);

    useEffect(() => {
        if (id) {
            setPost(getPostBySlug(id as string));
            void load(id as string);
        }
    }, [getPostBySlug, id, load]);

    return (
        <>
            {post !== null && (
                <HeadCustom>
                    <meta property="og:title" content={post.title ?? siteName} />
                    <meta
                        property="og:description"
                        content={
                            `${post?.excerpt?.replace(/<[^>]*>?/gm, '').substr(0, 190) ?? ''}...` ??
                            description
                        }
                    />
                    <meta property="og:image" content={post.imageUrl ?? ''} key="og:description" />
                    <meta property="og:url" content="https://adrianstanek.dev" />
                </HeadCustom>
            )}

            <Header />

            <div className="relative m-auto w-full max-w-screen-xl p-0 sm:flex sm:flex-col xl:grid xl:grid-cols-12 xl:gap-5 xl:p-0 ">
                <div className="relative col-span-8 mt-6 text-gray-500">
                    <>
                        <div className="relative w-full p-3 sm:left-0 2xl:-left-10">
                            {/* Headline */}
                            <h1 className="relative mb-1 mt-10 block font-serif text-2xl leading-8 tracking-tight text-secondary text-gray-900 sm:text-4xl md:text-3xl">
                                {post?.title}
                            </h1>

                            <div className="relative flex flex-row">
                                <div className="font-serif text-sm text-gray-400">
                                    {dayjs(post?.date).format('DD/MM/YYYY')}
                                </div>
                                <div className="px-2 text-primary">/</div>
                                <div className="font-serif text-sm text-gray-400">
                                    {readMinutes} Minute{readMinutes === 1 ? '' : 'n'} reading time
                                </div>
                            </div>
                        </div>

                        <PostAvatar
                            name={post?.author.name ?? ''}
                            description={post?.author.description ?? ''}
                        />

                        {post !== undefined && post?.imageUrl && (
                            <div
                                id="featuredImage"
                                className="aspect-w-16  aspect-h-9 relative border-gray-200 shadow-md"
                            >
                                {/* Tagline */}
                                <div className="absolute bottom-1 right-1 z-10 col-span-10 flex h-10 w-full w-full flex-row flex-wrap items-end justify-end p-3">
                                    {post.tags.map((tag) => (
                                        <PostTag label={tag ?? ''} primary key={nanoid()} />
                                    ))}
                                </div>

                                <Image
                                    src={post?.imageUrl ?? ''}
                                    alt={post?.title ?? ''}
                                    layout="fill"
                                    objectFit="cover"
                                    quality="80"
                                    loading="lazy"
                                    placeholder="blur"
                                    blurDataURL="images/asbg_flat.jpg"
                                />
                            </div>
                        )}
                        {post && post.imageDescription !== '' && (
                            <div className="flex w-full items-end justify-end">
                                <div className="w-full bg-white pt-1 pr-1 text-right text-xs font-thin text-neutral-500 shadow-md md:ml-10">
                                    {post.imageDescription ?? ''}
                                </div>
                            </div>
                        )}

                        {/* Actual Content */}
                        <div className="relative ml-0 bg-white p-3 pt-3 pb-12 shadow-md md:ml-10 md:p-10">
                            <div className="relative mb-2 flex items-center justify-end">
                                <ShareRow />
                            </div>

                            <div
                                id="fontSizeControl"
                                className="flex hidden w-full cursor-pointer flex-row items-end justify-end p-2 text-gray-400"
                            >
                                <FontAwesomeIcon
                                    className="bottom-0 mr-2 transform text-base text-primary transition-all ease-in-out hover:text-primary"
                                    icon={faFont}
                                />
                                <FontAwesomeIcon
                                    className="bottom-0 transform text-xl transition-all ease-in-out hover:text-primary hover:shadow-md"
                                    icon={faFont}
                                />
                            </div>
                            <div
                                id="excerpt"
                                className="mb-10 text-lg text-secondary"
                                dangerouslySetInnerHTML={{ __html: post?.excerpt ?? '' }}
                            />

                            <div className="relative mx-auto mb-6 flex w-16 flex-row items-center justify-evenly md:mb-12">
                                <FontAwesomeIcon
                                    className="text-xs text-gray-200"
                                    icon={faCircle}
                                />
                                <FontAwesomeIcon
                                    className="text-xs text-gray-400"
                                    icon={faCircle}
                                />
                                <FontAwesomeIcon
                                    className="text-xs text-gray-200"
                                    icon={faCircle}
                                />
                            </div>

                            {content && (
                                <div id="content" className="wpContent mb-10 text-base">
                                    <Markdown>{content}</Markdown>
                                </div>
                            )}
                            {/* Abbinder */}
                            <FounderBlock />
                        </div>
                    </>
                </div>
                <div className="col-span-4 flex flex-col justify-start ">
                    <div id="bumper" className="h-10 sm:hidden md:block md:h-56" />

                    <SidebarPost />
                </div>
            </div>

            <Footer />
        </>
    );
};

export default Post;
